import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Droplets, FileText, ArrowRight, Search, Inbox } from "lucide-react";

export default function InvoiceHistory() {
  const [invoices, setInvoices] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/invoices").then(({ data }) => setInvoices(data)).catch(() => setInvoices([]));
  }, []);

  const filtered = (invoices || []).filter((inv) => {
    const s = q.toLowerCase().trim();
    if (!s) return true;
    return [inv.invoice_number, inv.matter_number, inv.purchaser_name, inv.property_address]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(s));
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-400">Document Library</span>
          <h1 className="font-display text-3xl font-600 tracking-tight text-slate-900 mt-1">
            Invoices &amp; Certificates
          </h1>
          <p className="text-slate-500 text-sm mt-1">View and re-download any document you've generated.</p>
        </motion.div>

        <div className="mt-8 relative max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="invoice-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by invoice #, matter, purchaser or address…"
            className="w-full pl-9 pr-3 h-11 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500"
          />
        </div>

        <div className="mt-6 space-y-3" data-testid="invoice-list">
          {invoices === null ? (
            <div className="py-16 text-center">
              <Droplets className="w-7 h-7 mx-auto animate-pulse text-sky-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-400 bg-white border border-dashed border-slate-200 rounded-xl">
              <Inbox className="w-8 h-8 mx-auto mb-3" />
              <p className="text-sm">
                {invoices.length === 0 ? "No invoices generated yet." : "No documents match your search."}
              </p>
            </div>
          ) : (
            filtered.map((inv) => (
              <Link
                key={inv.id}
                to={`/contractor/invoices/${inv.id}`}
                data-testid={`invoice-row-${inv.id}`}
                className="group bg-white border border-slate-200 rounded-xl p-5 flex items-center justify-between hover:border-slate-300 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-sky-500" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-700 text-slate-900">{inv.invoice_number}</span>
                      {inv.matter_number && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                          Matter {inv.matter_number}
                        </span>
                      )}
                      {inv.urgent_fee > 0 && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-50 text-red-500">
                          Urgent
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5 truncate">{inv.property_address}</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {inv.purchaser_name} • {inv.date}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="font-display font-700 text-lg text-slate-900">${inv.total}</span>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-sky-500 transition-colors" />
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
